/**
 * Contextual help mapping -- which help article a screen's Help entry point
 * should open.
 *
 * `docs/user/HELP-SYSTEM-DESIGN.md` listed "contextual per-page mapping" as
 * deferred; this is that, kept deliberately small. It is a nav-target
 * computation, not a subsystem: one declarative table plus one pure function.
 *
 * ADDING A MAPPING IS ONE LINE. Put the route's `name` (not its path -- names
 * survive the path edits and `withPrefix()` rewrites that this router does
 * constantly) in HELP_CONTEXT_MAP with the audience and slug of the article it
 * should open. Nothing else needs to change.
 *
 * TWO OPTIONAL FIELDS take an entry from "the right article" to "the right
 * PLACE in the right article", for the two shapes a screen comes in:
 *
 *   - `heading` -- a heading anchor within the article, appended as a URL
 *     fragment. Anchors are rendered on every heading by `slugify_heading`
 *     (`api/src/utils/help_markdown.py`) and derived from the heading TEXT,
 *     so NO authoring work is needed to create one -- but renaming a heading
 *     MOVES its anchor, which is why the anti-rot test exists (below).
 *
 *   - `sections` -- a sub-map keyed on `route.params.section`, for a route
 *     that is really several screens. The store editor is thirteen sections
 *     behind one route name, and pointing all thirteen at the top of one
 *     generic article is most of what this file got wrong. A section entry
 *     may carry its own `slug` (a different article entirely), its own
 *     `heading`, or both; anything it omits falls back to the route entry.
 *
 * Both are optional and the flat shape is unchanged -- most of the entries
 * below neither need nor have them.
 *
 * THREE INVARIANTS, enforced by `__tests__/helpContextMap.spec.js` and
 * `__tests__/help-heading-targets.spec.js`:
 *
 *   1. Every slug here resolves to a real file in `docs/user/<audience>/`.
 *      A mapping to an article nobody wrote is WORSE than no mapping -- the
 *      fallback below guarantees a valid page, but a bad mapping routes past
 *      it into a 404, and does so silently at exactly the moment the reader
 *      asked for help. The spec reads the real docs tree off disk, so
 *      deleting an article without deleting its mapping fails the suite.
 *
 *   2. Every `heading` resolves to a real anchor in that article, re-derived
 *      from the markdown on disk. Same reasoning one level finer: a stale
 *      heading target does not 404, it silently lands at the TOP of the
 *      article, which reads to the user as the feature being broken. That is
 *      harder to notice than a 404, not easier.
 *
 *   3. Unmapped routes fall back to the audience index, and an unmapped
 *      SECTION falls back to its route's entry. There is no such thing as a
 *      dead Help link; the worst case is the reader landing on the list of
 *      articles and picking one.
 *
 * Only routes with an article written for them are mapped. Coverage grows
 * with the content, never ahead of it -- a route whose surface has no
 * truthful article stays unmapped and falls back to the index, which is why
 * the Payments page and the Notifications page are absent below despite
 * being in the sidebar (see the money comment, and `docs/user/owner/README.md`
 * "Deliberately not documented").
 */

/** Mount points, from the three help page components. */
export const OWNER_HELP_BASE = "/distributor/help/owner";
export const SHOPPER_HELP_BASE = "/help/shopper";
// SuperAdmin help mounts inside the `/admin` group so it inherits the SA
// chrome and the SA auth boundary -- the same reasoning that puts owner help
// under `/distributor`. It is staff documentation, never public.
export const SUPERADMIN_HELP_BASE = "/admin/help/superadmin";

const AUDIENCE_BASE = {
    owner: OWNER_HELP_BASE,
    shopper: SHOPPER_HELP_BASE,
    superadmin: SUPERADMIN_HELP_BASE,
};

const SAFE_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;
const SAFE_HEADING_RE = /^[a-z0-9-]*$/;

/**
 * The help path for an explicit { audience, slug, heading } -- the building
 * block `HelpIcon` targets use (the route resolver below builds the same
 * shape from the map). Same bases, same `/slug#heading` form, so there is
 * still exactly one place that knows what a help URL looks like. An unknown
 * audience yields the owner base, matching `resolveHelpTarget`'s fallback.
 */
export function helpTargetPath({ audience, slug, heading }) {
    const base = AUDIENCE_BASE[audience] || OWNER_HELP_BASE;
    // Defense in depth: props are static literals checked by the anti-rot
    // spec, but this builds a URL, so refuse anything outside the charset the
    // server's slugs use ('..', '//', '?', '#', backslash, uppercase, space)
    // and fall back to the audience index rather than a crafted path.
    if (typeof slug !== "string" || !SAFE_SLUG_RE.test(slug)) return base;
    if (heading !== undefined && heading !== null && heading !== "") {
        if (typeof heading !== "string" || !SAFE_HEADING_RE.test(heading)) {
            return base;
        }
    }
    return heading ? `${base}/${slug}#${heading}` : `${base}/${slug}`;
}

/**
 * Route name -> { audience, slug }.
 *
 * The audience is part of the entry rather than inferred from the route
 * because it is what keeps the two surfaces from bleeding into each other:
 * `resolveHelpTarget` refuses an entry whose audience does not match the
 * surface asking, so a shopper mapping can never link a distributor out of
 * the authenticated app (or the reverse).
 */
export const HELP_CONTEXT_MAP = {
    // --- Owner: building and launching a store -------------------------
    distributor_stores: { audience: "owner", slug: "build-a-store" },
    store_add: { audience: "owner", slug: "build-a-store" },

    // The store editor is THIRTEEN screens behind one route name, with the
    // section in `route.params.section` (`SECTION_IDS` in
    // `pages/distributor/store-editor/sectionRoutes.js`). The route-level
    // slug here is the fallback and is right for the bare `/edit` URL --
    // that is the Review & Launch landing, and `build-a-store` is the
    // article about the launch gate.
    //
    // Each section below points at the article a reader ON THAT SCREEN
    // needs, which is usually NOT build-a-store: eight sections have their
    // own article, and two more are documented inside an article about
    // something else.
    //
    // A section carries a `heading` only where the article covers more than
    // that one screen. Where the article IS the section end to end, the top
    // is already the right landing and a heading would skip the article's
    // own framing -- so those entries deliberately have none.
    //
    // WHY A SUB-MAP AND NOT THIRTEEN ORDINARY ENTRIES: `store_edit` is a
    // SINGLE route name with a `:section?` param (`router/index.js`), so
    // there is no route name to key thirteen flat entries on. The URLs are
    // already per-section (`/stores/:id/edit/pricing`); it is only the name
    // that is shared.
    //
    // Splitting the store editor into per-section routes is live as a future
    // possibility. If that happens each section gets its OWN route name, and
    // this sub-map folds into the main map above: every row below becomes an
    // ordinary flat entry (add `audience: "owner"`, key it on the new route
    // name), and `resolveSection` plus the `sections` field can be deleted
    // outright. Nothing else changes -- which is why the rows are a plain
    // {section: {slug, heading}} lookup and `resolveSection` branches on NO
    // section name. The targeting decisions are the durable asset here; the
    // param lookup is the disposable part, and they are kept separable on
    // purpose.
    store_edit: {
        audience: "owner",
        slug: "build-a-store",
        sections: {
            // --- Setup ---------------------------------------------------
            // Name + client is the first heading of the appearance article.
            "client-store": {
                slug: "storefront-appearance",
                heading: "name-the-store-and-pick-its-client",
            },
            // Logo, brand color, hero carousel, welcome message.
            branding: {
                slug: "storefront-appearance",
                heading: "give-the-store-its-look",
            },
            // The whole article is this section: web address, the four
            // access modes, the completed-store lock.
            "store-login": { slug: "store-access" },
            // Defaults is documented in the decoration-sets article (it is
            // where the default SET lives), and that article names this
            // section explicitly -- including that it is reachable only from
            // the Review & Launch grid, not the sidebar.
            defaults: {
                slug: "decoration-sets",
                heading: "set-what-new-products-start-from",
            },

            // --- Catalog -------------------------------------------------
            // The article is the five-step product flow, start to finish.
            products: { slug: "set-up-a-product" },
            categories: {
                slug: "storefront-appearance",
                heading: "group-the-products-shoppers-browse",
            },
            decorations: { slug: "store-decorations" },

            // --- Money and Checkout --------------------------------------
            pricing: { slug: "price-a-store" },
            checkout: { slug: "set-up-checkout" },
            batches: { slug: "manage-batches" },
            //
            // Tax & Legal is documented inside `set-up-your-business`,
            // because a store's tax handling and the business's nexus are
            // the same subject. The heading is load-bearing HERE more than
            // anywhere else in this map: without it the reader lands on
            // Company Profile, three unrelated headings above the section
            // they asked about.
            "tax-legal": {
                slug: "set-up-your-business",
                heading: "tax-settings-on-a-store",
            },

            // --- People and Comms ----------------------------------------
            team: { slug: "store-team" },
            // The STORE editor's notifications, not the account-level
            // preference matrix -- that one is deliberately undocumented
            // (see the money comment below and `docs/user/owner/README.md`).
            notifications: { slug: "store-notifications" },
        },

        // Sections with no article to point at. Listed rather than simply
        // omitted so the coverage test can tell "nobody has decided yet"
        // from "decided: there is nothing truthful to link". They fall back
        // to the route slug, exactly as an unlisted section would.
        //
        // `batches` WAS the one content gap here. It is closed: the Batches
        // section now maps to `manage-batches` above. The gap existed because
        // release charged nothing while the UI implied it did (#2790), so
        // there was no truthful article to point at; #3089 wired the real
        // charge and made the screen documentable.
        //
        // Mapped to the article TOP rather than a heading, deliberately: the
        // Batches section is one screen that the whole article covers, so
        // there is no single heading that is a better landing point than the
        // opening explanation of why orders wait.
        undocumentedSections: [],
    },

    // --- Owner: what happens after a shopper buys ----------------------
    distributor_reporting_orders: { audience: "owner", slug: "route-an-order" },
    distributor_reporting_order_detail: {
        audience: "owner",
        slug: "route-an-order",
    },
    // Staff order entry (card #566), moved here from the legacy dashboard.
    distributor_reporting_order_new: {
        audience: "owner",
        slug: "place-an-order-for-a-shopper",
    },

    // --- Owner: the companies you sell to ------------------------------
    client_list: { audience: "owner", slug: "manage-clients" },
    client_detail: { audience: "owner", slug: "manage-clients" },

    // --- Owner: artwork, from upload to client sign-off ----------------
    //
    // The approval workspace moved OFF `route-an-order` and onto
    // `prepare-artwork`, which covers the queue, the filter chips, and the
    // per-state action set directly. `route-an-order` only ever mentioned
    // approvals in passing as a thing to do before launch.
    approval_workspace: { audience: "owner", slug: "prepare-artwork" },
    approval_workspace_detail: { audience: "owner", slug: "prepare-artwork" },
    decoration_library_list: { audience: "owner", slug: "prepare-artwork" },
    decoration_library_add: { audience: "owner", slug: "prepare-artwork" },
    decoration_library_edit: { audience: "owner", slug: "prepare-artwork" },
    library_artwork_detail: { audience: "owner", slug: "prepare-artwork" },

    // --- Owner: reading the numbers ------------------------------------
    //
    // `distributor_reporting_payouts` IS mapped (unlike the Payments page
    // below): the Payouts REPORT is a working read surface derived from
    // ThreadFlow's own order records, and `track-performance` documents it
    // -- including, explicitly, that it is not a Stripe payout statement.
    // Nothing on that page depends on the broken #2675 onboarding path.
    distributor_reporting_performance: {
        audience: "owner",
        slug: "track-performance",
    },
    distributor_reporting_payouts: {
        audience: "owner",
        slug: "track-performance",
    },

    // --- Owner: business-wide settings ---------------------------------
    distributor_account_company: {
        audience: "owner",
        slug: "set-up-your-business",
    },
    distributor_account_team: {
        audience: "owner",
        slug: "set-up-your-business",
    },
    distributor_account_team_invite: {
        audience: "owner",
        slug: "set-up-your-business",
    },
    distributor_account_defaults: {
        audience: "owner",
        slug: "set-up-your-business",
    },
    distributor_account_compliance: {
        audience: "owner",
        slug: "set-up-your-business",
    },

    // --- Owner: personal account ---------------------------------------
    distributor_personal_profile: { audience: "owner", slug: "your-profile" },

    // --- Owner: money --------------------------------------------------
    //
    // Payments (Stripe Connect, ThreadFlow billing, plan) has its own
    // article; Suppliers shares the set-up article's Suppliers section.
    // `distributor_reporting_payouts` is the payouts REPORT, mapped above.
    distributor_account_payments: { audience: "owner", slug: "get-paid" },
    distributor_account_suppliers: {
        audience: "owner",
        slug: "set-up-your-business",
    },

    // --- SuperAdmin: operating the platform day to day -----------------
    //
    // The two SA articles split by JOB, not by nav item:
    // `manage-the-platform` is "operate the platform day to day" and
    // `platform-settings` is "change how the platform behaves". The mappings
    // follow that split rather than the sidebar grouping.
    sa_dashboard: { audience: "superadmin", slug: "manage-the-platform" },
    sa_users: { audience: "superadmin", slug: "manage-the-platform" },
    sa_user_create: { audience: "superadmin", slug: "manage-the-platform" },
    sa_user_detail: { audience: "superadmin", slug: "manage-the-platform" },
    sa_clients: { audience: "superadmin", slug: "manage-the-platform" },
    sa_client_detail: {
        audience: "superadmin",
        slug: "manage-the-platform",
    },
    distributor_list: {
        audience: "superadmin",
        slug: "manage-the-platform",
    },
    sa_distributor_edit: {
        audience: "superadmin",
        slug: "manage-the-platform",
    },
    sa_audit_log: { audience: "superadmin", slug: "manage-the-platform" },
    sa_risk_watch: { audience: "superadmin", slug: "manage-the-platform" },
    sa_money: { audience: "superadmin", slug: "manage-the-platform" },
    // Plans and pricing is the one topic the two articles cross-link on; it
    // is mapped to the operating article, which is where the cards are
    // described as a thing you DO rather than a setting you change.
    sa_plans_pricing: {
        audience: "superadmin",
        slug: "manage-the-platform",
    },

    // --- SuperAdmin: changing how the platform behaves -----------------
    sa_settings: { audience: "superadmin", slug: "platform-settings" },
    sa_dpi_thresholds: { audience: "superadmin", slug: "platform-settings" },
    sa_placement_zone_classes: {
        audience: "superadmin",
        slug: "platform-settings",
    },
    sa_placement_zone_dimensions: {
        audience: "superadmin",
        slug: "platform-settings",
    },
    sa_access_control: {
        audience: "superadmin",
        slug: "platform-settings",
    },

    // --- SuperAdmin: the Payments pages under Money --------------------
    // Five screens, one article: `payments-operations` has a section for each
    // (distributor billing, decorator settlement, collections, decoration
    // invoices, order reconciliation). The order drill-down shares the lookup
    // page's mapping because it is the same screen with an order preloaded.
    sa_payments_distributors: {
        audience: "superadmin",
        slug: "payments-operations",
    },
    sa_payments_settlement: {
        audience: "superadmin",
        slug: "payments-operations",
    },
    sa_payments_collections: {
        audience: "superadmin",
        slug: "payments-operations",
    },
    sa_payments_reconciliation: {
        audience: "superadmin",
        slug: "payments-operations",
    },
    sa_payments_reconciliation_order: {
        audience: "superadmin",
        slug: "payments-operations",
    },
    sa_distributor_invoices: {
        audience: "superadmin",
        slug: "payments-operations",
    },
    // The Books page has its own article: it answers "what is ThreadFlow's
    // own revenue", not "who owes whom" (#669).
    sa_payments_books: {
        audience: "superadmin",
        slug: "books",
    },

    // DELIBERATELY UNMAPPED, same rule as the owner-side Payments page:
    // `sa_substrate_buckets`. It persists a value that nothing reads -- the
    // light/dark decision is made by the WCAG luminance classifier without
    // ever consulting the substrate bucket.
    // `docs/user/superadmin/README.md` lists it under "deliberately not
    // documented", and `platform-settings.md` does not mention the screen
    // even in passing, so a mapping would open an article that says nothing
    // about the surface the reader is looking at. It falls back to the
    // platform help index. When the control is wired to a real consumer,
    // document it in `platform-settings.md` and map it in the same change.
    //
    // (`sa_dpi_thresholds` used to be listed here. It is mapped above now:
    // the artwork resolution levels are read by the placement gate, default
    // action `warn`, and `platform-settings.md` documents them.)

    // --- Shopper: the whole purchase flow ------------------------------
    storefront_home: { audience: "shopper", slug: "place-an-order" },
    storefront_category: { audience: "shopper", slug: "place-an-order" },
    storefront_product: { audience: "shopper", slug: "place-an-order" },
    storefront_cart: { audience: "shopper", slug: "place-an-order" },
    storefront_order_confirmation: {
        audience: "shopper",
        slug: "place-an-order",
    },

    // --- Shopper: after the purchase -----------------------------------
    // Order history, order detail, and re-ordering. These routes exist only
    // for shoppers whose store signs them in personally; `your-orders.md`
    // opens by saying so, so a reader who arrived here from a store that
    // does NOT is not left thinking something is missing.
    storefront_orders_list: { audience: "shopper", slug: "your-orders" },
    storefront_order_detail: { audience: "shopper", slug: "your-orders" },

    // The account area. `your-account.md` opens by saying it only applies to
    // stores that sign shoppers in personally. `storefront_saved_payment_methods`
    // is deliberately unmapped: nothing links to that page, and no article
    // describes it.
    storefront_profile: { audience: "shopper", slug: "your-account" },
    storefront_saved_addresses: { audience: "shopper", slug: "your-account" },
};

/**
 * The path a surface's Help entry point should link to.
 *
 * Reads `route.name` to pick the entry and `route.params.section` to pick a
 * per-section override within it. The returned path may carry a `#heading`
 * fragment; it never carries a query, so `withHelpFramedFlag` can append one
 * after the fragment without either being lost.
 *
 * @param {object|null} route     the current route (Vue Router's `$route`)
 * @param {string} audience       the surface asking: "owner" | "shopper" |
 *                                "superadmin"
 * @returns {string}              always a valid help path, never undefined
 *
 * Returns the audience index for: no route, an unnamed route (Vue Router's
 * pre-resolution initial route has `name: null`), an unmapped route, a
 * cross-audience mapping, and an unknown audience. Returning a usable path in
 * every one of those cases is the point -- a `router-link` handed `undefined`
 * renders a link to the current page, which reads to a user as a Help button
 * that does nothing.
 */
export function resolveHelpTarget(route, audience) {
    const base = AUDIENCE_BASE[audience] || OWNER_HELP_BASE;

    const name = route && route.name;
    if (!name) return base;

    const entry = Object.prototype.hasOwnProperty.call(HELP_CONTEXT_MAP, name)
        ? HELP_CONTEXT_MAP[name]
        : undefined;
    if (!entry) return base;

    // Cross-audience guard: the two surfaces sit on opposite sides of the
    // distributor auth boundary, so a mapping is only ever honored by the
    // surface it was written for. This is checked ONCE, on the route entry,
    // and a section can only narrow within it -- sections carry no audience
    // of their own, so the sub-map cannot become a way around this.
    if (entry.audience !== audience) return base;

    const section = resolveSection(entry, route);

    // The heading follows the SLUG, not the entry. A section that overrides
    // only the article must not inherit the route's heading -- that anchor
    // was chosen for a different document and would almost certainly not
    // exist in this one, which is the silent lands-at-the-top failure the
    // anti-rot test exists to prevent. So an overriding slug resets the
    // heading to whatever that section declares, and nothing if it declares
    // none.
    const slug = section.slug || entry.slug;
    const heading = section.slug
        ? section.heading
        : section.heading || entry.heading;

    // The fragment is appended here rather than by the caller because
    // `withHelpFramedFlag` decorates this path with a query string, and a
    // fragment must precede a query for either to survive. Keeping both
    // pieces of URL knowledge on this side of the seam is what makes that
    // ordering something the resolver can guarantee.
    return heading
        ? `${base}/${slug}#${heading}`
        : `${base}/${slug}`;
}

/**
 * The per-section override for a route, or an empty object.
 *
 * A section id arrives from the URL, so it is untrusted in the ordinary way
 * a path segment is: `hasOwnProperty` rather than a bare lookup keeps
 * `/edit/constructor` from resolving to a function off Object.prototype and
 * being spread as though it were a mapping. Vue Router also hands repeated
 * params through as an ARRAY, which is not a valid key and must miss
 * cleanly rather than stringify into one.
 */
function resolveSection(entry, route) {
    const sections = entry.sections;
    if (!sections) return {};

    const section = route && route.params && route.params.section;
    if (typeof section !== "string" || !section) return {};

    if (!Object.prototype.hasOwnProperty.call(sections, section)) return {};

    return sections[section] || {};
}
